import { gqlRequest } from './gql-request';
import type {
  LatestReportQueryVariables,
  ProjectFragment,
  ProjectsQueryVariables,
  ReportByCommitQueryVariables,
  ReportFragment,
} from './graphql/generated';

export type PortalReportDownloadArgs = {
  /** GraphQL endpoint URL */
  server: string;
  /** API key with read permissions */
  apiKey: string;
  /** GraphQL query variables */
  parameters: LatestReportQueryVariables | ReportByCommitQueryVariables;
  /** GraphQL request timeout in minutes */
  timeout?: number;
};

/**
 * Downloads report from Code PushUp portal via GraphQL query. Looks for latest report on default branch, unless `params.commit` is provided.
 * @param args GraphQL endpoint configuration and query variables.
 * @returns Report (if it exists), with or without audit details (based on `params.withAuditDetails` flag).
 */
export async function downloadReportFromPortal(
  args: PortalReportDownloadArgs,
): Promise<ReportFragment | undefined> {
  const { server, apiKey, parameters: variables, timeout } = args;

  if ('commit' in variables) {
    const data = await gqlRequest({
      operation: 'reportByCommit',
      server,
      apiKey,
      variables,
      timeout,
    });
    return data.report ?? undefined;
  }

  const data = await gqlRequest({
    operation: 'latestReport',
    server,
    apiKey,
    variables,
    timeout,
  });
  return data.project?.latestReport ?? undefined;
}

export type PortalProjectsDownloadArgs = {
  /** GraphQL endpoint URL */
  server: string;
  /** API key with read permissions */
  apiKey: string;
  /** GraphQL query variables */
  parameters?: ProjectsQueryVariables;
  /** GraphQL request timeout in minutes */
  timeout?: number;
};

/**
 * Downloads all projects from Code PushUp portal via GraphQL query. Projects may include their latest report. Projects can be filtered by organization.
 * @param args GraphQL endpoint configuration and query variables.
 * @returns All projects in portal with or without latest report (based on `params.latestReport` flag) and audit details (based on `params.withAuditDetails` flag).
 */
export async function downloadProjectsFromPortal(
  args: PortalProjectsDownloadArgs,
): Promise<ProjectFragment[]> {
  const { server, apiKey, parameters: variables, timeout } = args;
  const data = await gqlRequest({
    operation: 'projects',
    server,
    apiKey,
    variables,
    timeout,
  });
  return data.projects;
}
